/**
 * /docs/examples — four prompt → outcome examples spanning the shapes
 * Kin handles: static, database-backed, API-integrated, and scheduled.
 */

import { PageHeader } from '../../components/ui.js';
import { DocsTabs } from '../../components/DocsTabs.js';
import { Callout, CodeBlock, DocSection } from './_shared.js';

export function DocsExamples() {
  return (
    <div className="animate-fade-in">
      <PageHeader
        eyebrow="Docs · Examples"
        title="Real prompts, real apps"
        description="Four shapes. Copy a prompt, tweak for your case, ship."
      />

      <DocsTabs />

      <Callout title="Two ways to paste these">
        <p>
          These prompts open with <em>&ldquo;Make me a Kin app to ...&rdquo;</em> &mdash; that
          phrasing kicks off app creation in <strong>Claude Code</strong>, where the word{' '}
          <em>Kin</em> activates the skill. If you&apos;re in the <strong>in-browser chat</strong>{' '}
          instead, the app already exists &mdash; create it via{' '}
          <a className="underline" href="/apps/new">
            Apps &rarr; Start building
          </a>
          , then paste the description part (everything after &ldquo;to&rdquo;) into the chat
          bubble.
        </p>
      </Callout>

      <DocSection title="1. Static lookup tool — no backend">
        <h3 className="font-medium text-ink">Prompt</h3>
        <CodeBlock
          body={`Make me a Kin app that lists every Mytra office and conference-room wifi
network with its password and any quirks (e.g., "needs ethernet for prints").
Searchable by office or network name. Just the lookup — no editing,
no sign-up.`}
          caption="prompt"
        />

        <h3 className="font-medium text-ink mt-4">What you get</h3>
        <ul className="list-disc list-inside space-y-1 pl-1">
          <li>
            <strong>Slug:</strong> <span className="font-mono text-xs">wifi-lookup</span>
          </li>
          <li>
            <strong>URL:</strong>{' '}
            <span className="font-mono text-xs">https://wifi-lookup.mkin.app</span>
          </li>
          <li>
            <strong>Shape:</strong> Single-page table with a search box. Data baked into the bundle
            as JSON. To add a row, re-prompt:{' '}
            <em>&ldquo;add the NYC office, network &lsquo;mytra-guest-nyc&rsquo;...&rdquo;</em>
          </li>
          <li>
            <strong>Access:</strong> Anyone at Mytra.
          </li>
        </ul>
        <Callout tone="tip" title="When to skip the backend">
          <p>
            Static wins when the data changes rarely (less than weekly) and only by you. Faster to
            load, impossible to corrupt, no migrations.
          </p>
        </Callout>
      </DocSection>

      <DocSection title="2. Lunch sign-ups — shared database">
        <h3 className="font-medium text-ink">Prompt</h3>
        <CodeBlock
          body={`Make me a Kin app to track who's bringing what to team lunch each
Friday. People pick the upcoming Friday from a dropdown, type their
name, and what they're bringing. Show a table grouped by date.
Anyone at Mytra can add. No editing once submitted — but let people
delete their own entry.`}
          caption="prompt"
        />

        <h3 className="font-medium text-ink mt-4">What you get</h3>
        <ul className="list-disc list-inside space-y-1 pl-1">
          <li>
            <strong>Slug:</strong> <span className="font-mono text-xs">lunch-signups</span>
          </li>
          <li>
            <strong>URL:</strong>{' '}
            <span className="font-mono text-xs">https://lunch-signups.mkin.app</span>
          </li>
          <li>
            <strong>Shape:</strong> Form on top, table below. SQLite behind it. The AI generates the
            schema after you confirm persistence.
          </li>
          <li>
            <strong>Access:</strong> Anyone at Mytra; identity comes from your Mytra sign-in, so
            users can only delete their own.
          </li>
          <li>
            <strong>Next iteration:</strong>{' '}
            <em>&ldquo;also let people mark themselves attending&rdquo;</em> &mdash; the AI adds a
            column and migrates existing data.
          </li>
        </ul>
      </DocSection>

      <DocSection title="3. Open Linear bugs — external API">
        <h3 className="font-medium text-ink">Prompt</h3>
        <CodeBlock
          body={`Build a Kin app that shows our currently-open Linear issues, grouped by
team. Use a Linear API token from a secret called LINEAR_API_KEY — I'll
add the value in the Kin portal after you deploy. Refresh on page load
is fine, no live updates needed.`}
          caption="prompt"
        />

        <h3 className="font-medium text-ink mt-4">What you get</h3>
        <ul className="list-disc list-inside space-y-1 pl-1">
          <li>
            <strong>Slug:</strong> <span className="font-mono text-xs">linear-bugs</span>
          </li>
          <li>
            <strong>URL:</strong>{' '}
            <span className="font-mono text-xs">https://linear-bugs.mkin.app</span>
          </li>
          <li>
            <strong>Shape:</strong> On load, calls Linear&apos;s API server-side so the key never
            reaches the browser. Filters to open, groups by team. No database &mdash; Linear is
            source of truth.
          </li>
          <li>
            <strong>Your job:</strong> open <em>this portal &rarr; linear-bugs &rarr; Secrets</em>,
            paste the key as <span className="font-mono text-xs">LINEAR_API_KEY</span>, save. Next
            page load, data appears.
          </li>
        </ul>
        <Callout title="The AI never sees the secret value">
          <p>
            It writes code referencing <span className="font-mono text-xs">env.LINEAR_API_KEY</span>
            . The value is encrypted in Kin&apos;s storage and decrypted at runtime by your app
            &mdash; not by the AI.
          </p>
        </Callout>
      </DocSection>

      <DocSection title="4. Weekly on-call digest — scheduled">
        <h3 className="font-medium text-ink">Prompt</h3>
        <CodeBlock
          body={`Make a Kin app that posts the on-call rotation for the coming week to
#eng-oncall in Slack every Monday at 8am Pacific. Read the rotation from
PagerDuty — I'll add a PAGERDUTY_TOKEN secret. Slack webhook URL goes
in a SLACK_WEBHOOK_URL secret.`}
          caption="prompt"
        />

        <h3 className="font-medium text-ink mt-4">What you get</h3>
        <ul className="list-disc list-inside space-y-1 pl-1">
          <li>
            <strong>Slug:</strong> <span className="font-mono text-xs">oncall-digest</span>
          </li>
          <li>
            <strong>URL:</strong>{' '}
            <span className="font-mono text-xs">https://oncall-digest.mkin.app</span> &mdash; shows
            the last sent message and a &ldquo;send now&rdquo; test button.
          </li>
          <li>
            <strong>Shape:</strong> Scheduled task{' '}
            <span className="font-mono text-xs">(0 16 * * 1)</span> &mdash; the AI handles the
            timezone math.
          </li>
          <li>
            <strong>Access:</strong> Private to you.
          </li>
          <li>
            <strong>Verify it works:</strong> ask the AI to{' '}
            <em>&ldquo;run the schedule now.&rdquo;</em> Slack message lands within a minute.
          </li>
        </ul>
      </DocSection>

      <DocSection title="More ideas">
        <ul className="list-disc list-inside space-y-1.5 pl-1">
          <li>
            <strong>Desk-height calculator</strong> &mdash; input your height, get desk and chair
            recommendations. Static.
          </li>
          <li>
            <strong>Suggestion box</strong> &mdash; anonymous ops suggestions visible to managers.
            Backend.
          </li>
          <li>
            <strong>Team birthdays</strong> &mdash; pulls from a Google Sheet, shows the next month.
            Sheet API + secret.
          </li>
          <li>
            <strong>Friday-shipped list</strong> &mdash; weekly digest of what merged to #shipped.
            Scheduled + GitHub API.
          </li>
          <li>
            <strong>Plant watering</strong> &mdash; table of plants with a &ldquo;mark
            watered&rdquo; button. Backend.
          </li>
          <li>
            <strong>Vendor lookup</strong> &mdash; paste an invoice, get vendor info. Slack slash
            command + static CSV.
          </li>
        </ul>
      </DocSection>
    </div>
  );
}
